import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { CheckCircle2, XCircle, MinusCircle, TrendingUp, Sparkles, Ticket, Lightbulb, Target } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, V1 } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { pickModuleAccess } from "@/lib/moduleAccess";

export default function ExamResult() {
  const { sessionId } = useParams();
  const [data, setData] = useState(null);
  const [solutions, setSolutions] = useState({}); // {qid: {solution, source, access}}
  const [loading, setLoading] = useState({});
  const { user } = useAuth();
  // Abonelik (unlimited/aylık kota) varsa "Çözümü Göster" butonu kredi göstermez —
  // kredi rozeti sadece abonelik yoksa/kota tükendiğinde fallback olarak görünür.
  const [solveAccess, setSolveAccess] = useState({ free: false, cost: 1 });

  useEffect(() => {
    api.get(`${V1}/exam-sessions/${sessionId}/result`).then((r) => setData(r.data)).catch(() => {});
    if (user) {
      api.get(`${V1}/users/me/usage`).then((r) => setSolveAccess(pickModuleAccess(r.data, "soru_cozumu"))).catch(() => {});
    }
  }, [sessionId, user]);

  const showSolution = async (qid) => {
    if (solutions[qid]) return; // already visible
    if (!user) { toast.error("Çözüm için giriş yapman gerekli"); return; }
    setLoading((s) => ({ ...s, [qid]: true }));
    try {
      const { data: sol } = await api.post(`${V1}/questions/${qid}/solve`);
      setSolutions((s) => ({ ...s, [qid]: sol }));
      if (sol.charged_tx_id) toast.success("1 kredi kullanıldı");
      else if (sol.access?.source === "subscription" || sol.access?.source === "subscription_quota") toast.success("Aboneliğinle ücretsiz açıldı ✓");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Çözüm alınamadı");
    } finally {
      setLoading((s) => ({ ...s, [qid]: false }));
    }
  };

  const { weak, strong } = useMemo(() => {
    const review = data?.review || [];
    // Attach the original question number before reordering so numbering stays meaningful.
    const numbered = review.map((r, i) => ({ ...r, _originalNo: i + 1 }));
    const w = numbered.filter((r) => !r.is_correct);
    const s = numbered.filter((r) => r.is_correct);
    return { weak: w, strong: s };
  }, [data]);

  if (!data) return <div className="p-16 text-center text-muted-foreground">Sonuç yükleniyor…</div>;

  return (
    <div className="mx-auto max-w-5xl px-4 py-12" data-testid="exam-result-page">
      <div className="text-center mb-10">
        <div className="overline text-accent mb-2">Sınav Sonucu</div>
        <h1 className="font-display text-4xl font-bold">Tebrikler, sınavın tamamlandı.</h1>
      </div>
      <div className="grid md:grid-cols-4 gap-4 mb-10">
        <ResultStat icon={CheckCircle2} tone="success" label="Doğru" value={data.correct} />
        <ResultStat icon={XCircle} tone="destructive" label="Yanlış" value={data.incorrect} />
        <ResultStat icon={MinusCircle} tone="muted" label="Boş" value={data.unanswered} />
        <ResultStat icon={TrendingUp} tone="accent" label="Net" value={data.net_score?.toFixed(2)} />
      </div>
      {Object.keys(data.subject_averages || {}).length > 0 && (
        <div className="p-6 rounded-xl border border-border bg-card mb-10">
          <div className="font-display font-bold text-lg mb-4">Ders Bazında Başarı</div>
          <div className="space-y-3">
            {Object.entries(data.subject_averages).map(([subj, val]) => (
              <div key={subj}>
                <div className="flex justify-between text-sm mb-1">
                  <span>{subj}</span>
                  <span className="font-mono font-semibold">%{val}</span>
                </div>
                <div className="h-2 bg-muted rounded">
                  <div className="h-full bg-primary rounded" style={{ width: `${val}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="p-6 rounded-xl border border-accent/40 bg-accent/5 mb-6" data-testid="weak-questions-section">
        <div className="font-display font-bold text-lg mb-1 flex items-center gap-2">
          <Target className="h-5 w-5 text-accent" /> Önce Bunlara Bak · Yanlış &amp; Boş Sorular
        </div>
        <p className="text-xs text-muted-foreground mb-4">Zayıf olduğun konuları hızlıca görmen için yanlış ve boş bıraktığın sorular en üste alındı.</p>
        {weak.length === 0 ? (
          <div className="text-sm text-muted-foreground py-4 text-center">Tebrikler, hiç yanlışın veya boşun yok! 🎉</div>
        ) : (
          <div className="space-y-4">
            {weak.map((r, i) => (
              <QuestionReviewCard key={r.id} r={r} sol={solutions[r.id]} loading={loading[r.id]} onSolve={showSolution} testid={`review-q-${i}`} solveAccess={solveAccess} />
            ))}
          </div>
        )}
      </div>
      {strong.length > 0 && (
        <div className="p-6 rounded-xl border border-border bg-card mb-10" data-testid="correct-questions-section">
          <div className="font-display font-bold text-lg mb-4">Doğru Cevapladıkların</div>
          <div className="space-y-4">
            {strong.map((r, i) => (
              <QuestionReviewCard key={r.id} r={r} sol={solutions[r.id]} loading={loading[r.id]} onSolve={showSolution} testid={`review-q-${weak.length + i}`} solveAccess={solveAccess} />
            ))}
          </div>
        </div>
      )}
      <div className="flex flex-wrap gap-3 justify-center">
        <Button asChild><Link to="/panel/performans">Performansımı Gör</Link></Button>
        <Button asChild variant="outline"><Link to="/deneme-sinavlari">Başka Deneme Sınavı</Link></Button>
      </div>
    </div>
  );
}

const QuestionReviewCard = ({ r, sol, loading, onSolve, testid, solveAccess }) => (
  <div className="p-4 rounded border border-border" data-testid={testid}>
    <div className="flex items-start justify-between gap-3 mb-2">
      <div className="text-sm font-semibold">Soru {r._originalNo}. {r.subject}</div>
      {r.is_correct ? (
        <span className="text-xs px-2 py-0.5 bg-success/10 text-success rounded">Doğru</span>
      ) : r.user_answer ? (
        <span className="text-xs px-2 py-0.5 bg-destructive/10 text-destructive rounded">Yanlış</span>
      ) : (
        <span className="text-xs px-2 py-0.5 bg-muted text-muted-foreground rounded">Boş</span>
      )}
    </div>
    <div className="text-sm mb-2 whitespace-pre-line">{r.text}</div>
    <div className="grid gap-1 text-sm">
      {r.options.map((o) => (
        <div key={o.key} className={`px-3 py-1.5 rounded border ${
          o.key === r.correct_option ? "border-success bg-success/5" :
          r.user_answer === o.key ? "border-destructive bg-destructive/5" :
          "border-transparent"
        }`}>
          <b>{o.key}.</b> {o.text}
        </div>
      ))}
    </div>
    {r.explanation && (
      <div className="mt-2 text-xs text-muted-foreground bg-muted p-2 rounded">
        <b>Açıklama:</b> {r.explanation}
      </div>
    )}
    <div className="mt-3">
      {sol ? (
        <div className="p-3 rounded-lg border border-accent/40 bg-accent/5" data-testid={`solution-${r.id}`}>
          <div className="flex items-center gap-1 text-xs text-accent font-medium mb-2">
            <Sparkles className="h-3 w-3" /> Adım-adım Çözüm
            {sol.source === "admin" && <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-muted text-muted-foreground">öğretmen</span>}
            {sol.access?.source === "subscription" && <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-success/10 text-success">abonelik</span>}
          </div>
          <div className="text-sm whitespace-pre-line leading-relaxed">{sol.solution}</div>
        </div>
      ) : (
        <Button
          variant="outline" size="sm"
          onClick={() => onSolve(r.id)}
          disabled={loading}
          data-testid={`show-solution-btn-${r.id}`}
        >
          {loading ? (
            "Hazırlanıyor…"
          ) : solveAccess?.free ? (
            <><Lightbulb className="h-4 w-4" /> Çözümü Göster <span className="text-[10px] opacity-60">(Aboneliğine Dahil)</span></>
          ) : (
            <><Lightbulb className="h-4 w-4" /> Çözümü Göster <Ticket className="h-3 w-3 opacity-60 ml-1" /> <span className="text-[10px] opacity-60">{solveAccess?.cost ?? 1} kredi</span></>
          )}
        </Button>
      )}
    </div>
  </div>
);

const ResultStat = ({ icon: Icon, tone, label, value }) => {
  const map = {
    success: "text-success bg-success/10",
    destructive: "text-destructive bg-destructive/10",
    muted: "text-muted-foreground bg-muted",
    accent: "text-accent bg-accent/10",
  };
  return (
    <div className="p-6 rounded-xl border border-border bg-card">
      <div className={`inline-flex h-10 w-10 items-center justify-center rounded-md ${map[tone]}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div className="font-display text-3xl font-bold mt-3">{value}</div>
      <div className="text-xs uppercase tracking-widest text-muted-foreground mt-1">{label}</div>
    </div>
  );
};
